import React from "react";
import { styled } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Divider from "@mui/material/Divider";
import colors from "assets/theme-dark/base/colors";
import Card from "@mui/material/Card";

interface SqlPreviewProps {
  selectedColumns: Record<
    string,
    { selectedColumns: string[]; joinColumn: string | null; joinWith?: string; joinWithColumn?: string | null }
  >;
  tableOrder: string[];
  datasets: { id: string; name: string }[];
}

const sanitizeAlias = (name: string, index: number): string => {
  const cleanedName = name
    .replace(/[^a-zA-Z0-9\s_]/g, "")
    .replace(/\s+/g, "_")
    .trim();

  const baseAlias = !cleanedName || /^[0-9]/.test(cleanedName) ? `Table${index + 1}_${cleanedName}` : cleanedName;

  return baseAlias.substring(0, 30);
};

const generateUniqueAlias = (baseAlias: string, usedAliases: string[], suffix: number = 1): string => {
  const candidate = suffix === 1 ? baseAlias : `${baseAlias}_${suffix}`;
  return usedAliases.includes(candidate) ? generateUniqueAlias(baseAlias, usedAliases, suffix + 1) : candidate;
};

const SqlPreview: React.FC<SqlPreviewProps> = ({ selectedColumns, tableOrder, datasets }) => {
  if (!selectedColumns || !tableOrder || !datasets) {
    return (
      <Container>
        <StyledCard>
          <MDTypography variant="body2" color="error" sx={{ fontWeight: 500 }}>
            Invalid input data. Please check the props passed to SqlPreview.
          </MDTypography>
        </StyledCard>
      </Container>
    );
  }

  const hasSelections = tableOrder.some((datasetId) => selectedColumns[datasetId]?.selectedColumns?.length > 0);
  if (!hasSelections) {
    return (
      <Container>
        <StyledCard>
          <MDTypography variant="body2" color="text" sx={{ fontWeight: 500, opacity: 0.8 }}>
            No datasets or columns selected. Please select datasets and configure columns to generate the SQL query.
          </MDTypography>
        </StyledCard>
      </Container>
    );
  }

  const aliasMap: Record<string, string> = tableOrder.reduce((acc: Record<string, string>, datasetId, index) => {
    const dataset = datasets.find((d) => d.id === datasetId);
    const baseAlias = dataset ? sanitizeAlias(dataset.name, index) : `Table${index + 1}`;
    const usedAliases = Object.values(acc);
    const uniqueAlias = generateUniqueAlias(baseAlias, usedAliases);
    return { ...acc, [datasetId]: uniqueAlias };
  }, {});

  const selectClause = tableOrder
    .flatMap((datasetId) => {
      const alias = aliasMap[datasetId];
      const columns = selectedColumns[datasetId]?.selectedColumns || [];
      return columns.map((column) => `${alias}."${column}" AS ${alias}_${column}`);
    })
    .join(",\n  ");

  const joinClauses = tableOrder
    .slice(1)
    .map((datasetId) => {
      const alias = aliasMap[datasetId];
      const dataset = datasets.find((d) => d.id === datasetId);
      const joinWithId = selectedColumns[datasetId]?.joinWith;
      const joinColumn = selectedColumns[datasetId]?.joinColumn;
      const joinWithColumn = selectedColumns[datasetId]?.joinWithColumn;
      const joinWithDataset = joinWithId ? datasets.find((d) => d.id === joinWithId) : null;
      const joinWithIndex = joinWithId ? tableOrder.indexOf(joinWithId) : -1;
      const currentIndex = tableOrder.indexOf(datasetId);

      if (!joinColumn || !joinWithId || !joinWithDataset) {
        return `LEFT JOIN ${dataset?.name || "Unknown"} AS ${alias} ON ... /*! No join specified for ${
          dataset?.name || "Unknown"
        } */`;
      }

      if (!joinWithColumn || !joinColumn) {
        return `LEFT JOIN ${dataset?.name || "Unknown"} AS ${alias} ON TRUE /* Missing ${
          !joinWithColumn ? "'Join by'" : "'Join Column'"
        } for ${dataset?.name || "Unknown"} */`;
      }

      if (joinWithIndex >= currentIndex) {
        return `LEFT JOIN ${dataset?.name || "Unknown"} AS ${alias} ON TRUE /* Skipped: ${dataset?.name} → ${
          joinWithDataset.name
        } (later in order) */`;
      }

      const joinWithAlias = aliasMap[joinWithId];
      return `LEFT JOIN ${
        dataset?.name || "Unknown"
      } AS ${alias} ON ${joinWithAlias}."${joinWithColumn}" = ${alias}."${joinColumn}"`;
    })
    .join("\n");

  const rootDataset = datasets.find((d) => d.id === tableOrder[0]);
  const sqlQuery = `
SELECT
  ${selectClause}
FROM ${rootDataset?.name || "Unknown"} AS ${aliasMap[tableOrder[0]]}
${joinClauses}
  `.trim();

  return (
    <Container>
      <StyledCard>
        <MDTypography variant="h6" fontWeight="medium" mb={1} sx={{ color: colors.text.main, fontSize: "1.25rem" }}>
          Real-Time SQL Query Preview
        </MDTypography>
        <Divider
          sx={{
            margin: 0,
            marginBottom: "10px",
            borderColor: colors.grey[500],
            borderWidth: "1px",
          }}
        />
        <CodeBlock>
          {sqlQuery.split("\n").map((line, index) => (
            <MDTypography
              key={index}
              variant="body2"
              component="div"
              sx={{
                fontFamily: "'Roboto Mono', monospace",
                fontSize: "0.875rem",
                color: colors.text.main,
                lineHeight: 1.5,
              }}
            >
              {line}
            </MDTypography>
          ))}
        </CodeBlock>
      </StyledCard>
    </Container>
  );
};

const Container = styled(MDBox)`
  margin-top: 1rem;
`;

const StyledCard = styled(Card)`
  background: ${colors.background.default};
  box-shadow: ${({ theme }) => theme.shadows[3]};
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid ${colors.grey[300]};
`;

const CodeBlock = styled(MDBox)`
  border-radius: 8px;
  padding: 1rem;
  overflow-x: auto;
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${colors.grey[900]};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.primary.main};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${colors.primary.dark};
  }
`;

export default React.memo(SqlPreview);
